import { useMutation } from '@apollo/client';
import type { PromptType } from '@communities/prompt-management/domain';
import { GET_COMMUNITY_PROMPTS, UPDATE_PROMPT_PROD_VERSION } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useState, useCallback } from 'react';

export interface UpdatePromptProdVersionInput {
  version: string;
  type?: PromptType;
}

interface UpdatePromptProdVersionResponse {
  updatePromptProdVersion: {
    success: boolean;
    message: string;
  };
}

export function useUpdateProdPromptVersion() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updatePromptProdVersionMutation, { loading }] = useMutation<UpdatePromptProdVersionResponse>(UPDATE_PROMPT_PROD_VERSION, {
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updatePromptProdVersion = useCallback(
    async (communityId: string, input: UpdatePromptProdVersionInput): Promise<boolean> => {
      try {
        track('UpdateProdPromptVersionInitiated', {
          communityId,
          promptVersion: input.version,
        });

        await updatePromptProdVersionMutation({
          variables: {
            communityId,
            input,
          },
          refetchQueries: communityId ? [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }] : [],
          awaitRefetchQueries: true,
        });

        track('UpdateProdPromptVersionSucceeded', {
          communityId,
          promptVersion: input.version,
        });

        return true;
      } catch (error) {
        track('UpdateProdPromptVersionFailed', {
          communityId,
          promptVersion: input.version,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return false;
      }
    },
    [updatePromptProdVersionMutation]
  );

  return {
    updatePromptProdVersion,
    loading,
    errors,
    resetErrors,
  };
}
